import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { adminUsage } from "@/lib/api/usage";
import { useAdminAccess } from "@/lib/hooks/use-admin";

function eur(n: number): string {
  return `€${n.toFixed(n !== 0 && Math.abs(n) < 1 ? 4 : 2)}`;
}

function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return `${["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][m]} ${y}`;
}

const TYPE_LABELS: Record<string, string> = {
  ai: "AI Inference",
  sumsub: "Sumsub ID verification",
  trapets: "Trapets screening",
  whereby: "Whereby meetings",
  file_transcription: "File transcription",
  web: "Web search & fetch",
  enin: "ENIN lookup",
  idura: "Idura eID & signing",
  sms: "SMS",
};

export default function AdminUsagePage() {
  const [month, setMonth] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isLoading: accessLoading, canViewAdmin } = useAdminAccess();

  useEffect(() => {
    if (accessLoading) return;
    if (!canViewAdmin) {
      navigate("/", { replace: true });
    }
  }, [accessLoading, canViewAdmin, navigate]);

  const { data: months, isLoading } = useQuery({
    queryKey: ["admin-usage", "monthly"],
    queryFn: () => adminUsage.monthly(),
    enabled: canViewAdmin,
  });

  const { data: tenants } = useQuery({
    queryKey: ["admin-usage", "by-tenant", month],
    queryFn: () => adminUsage.byTenant(month as string),
    enabled: !!month,
  });

  const { data: byType } = useQuery({
    queryKey: ["admin-usage", "tenant-breakdown", tenantId, month],
    queryFn: () => adminUsage.tenantBreakdown(tenantId as string, month as string),
    enabled: !!tenantId && !!month,
  });

  const selectMonth = (m: string) => {
    setTenantId(null);
    setMonth(month === m ? null : m);
  };

  if (accessLoading || !canViewAdmin) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: "Admin", href: "/admin" }, { label: "Usage" }]}
        title="Metered usage"
        description="Metered 3rd-party + AI Inference cost per tenant per month. Prices in EUR, excl. VAT."
      />

      <Card>
        <CardHeader>
          <CardTitle>Monthly cost (all visible tenants)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !months || months.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No metered usage yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {months.map((m) => (
                  <TableRow
                    key={m.month}
                    className={`cursor-pointer ${month === m.month ? "bg-muted/50" : ""}`}
                    onClick={() => selectMonth(m.month)}
                  >
                    <TableCell className="font-medium">{monthLabel(m.month)}</TableCell>
                    <TableCell className="text-right tabular-nums">{eur(m.costEur)}</TableCell>
                    <TableCell>
                      <ChevronRight
                        className={`h-4 w-4 text-muted-foreground transition-transform ${month === m.month ? "rotate-90" : ""}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {month && tenants && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By tenant — {monthLabel(month)}</CardTitle>
          </CardHeader>
          <CardContent>
            {tenants.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No usage this month.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead className="text-right">Events</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((t) => (
                    <TableRow
                      key={t.tenantId}
                      className={`cursor-pointer ${tenantId === t.tenantId ? "bg-muted/50" : ""}`}
                      onClick={() => setTenantId(tenantId === t.tenantId ? null : t.tenantId)}
                    >
                      <TableCell className="font-medium">{t.tenantName}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {t.count}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{eur(t.costEur)}</TableCell>
                      <TableCell>
                        <ChevronRight
                          className={`h-4 w-4 text-muted-foreground transition-transform ${tenantId === t.tenantId ? "rotate-90" : ""}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {tenantId && byType && (
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle className="text-base">
              By feature — {tenants?.find((t) => t.tenantId === tenantId)?.tenantName ?? "tenant"}
            </CardTitle>
            {month && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => adminUsage.exportPdf(tenantId, month)}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Download PDF
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {byType.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No usage.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead className="text-right">Events</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byType.map((r) => (
                    <TableRow key={r.type}>
                      <TableCell>{TYPE_LABELS[r.type] ?? r.type}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {r.count}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{eur(r.costEur)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
